/**
 * Per-layer style modal — three tabs:
 *
 *   Single       Uniform color / opacity / size for every feature.
 *                Color picker = atlas swatches + free hex input.
 *
 *   Graduated    Classify a numeric column. For lines/points you can vary
 *                color, size, or both (size = circle-radius / line-width).
 *
 *   Categorical  Pick a string/bool column → assign a color per distinct
 *                value (auto-seeded from CATEGORICAL_PALETTE, override
 *                any with a swatch tap or hex).
 */
import { runBreaks, runCategories } from '../api/analytics.js';
import {
  RAMPS, RAMP_LABELS, sampleRamp, stepExpression, rampedSizes,
  CATEGORICAL_PALETTE, pickCategoricalColor, matchExpression,
  BIVARIATE_PALETTES, BIVARIATE_PALETTE_LABELS, bivariateExpression,
} from './colorRamps.js';
import { setStyle, clearStyle, getStyle } from './styleStore.js';
import { setLegend, setBivariateLegend, resetLegend } from './legend.js';
import { toast } from './toast.js';

const NUMERIC_DTYPES = ['int8','int16','int32','int64','float32','float64','bool'];
const isNumeric = (dtype) => NUMERIC_DTYPES.some(t => (dtype || '').includes(t));
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const PRESET_COLORS = [
  '#04648d', '#98c262', '#4ea3ce', '#e0a93a',
  '#d65a51', '#7a9bbb', '#b8d290', '#5b7d8c',
  '#053f5a', '#46622a', '#327c9f', '#bf4940',
  '#ffffff', '#b3c0c8', '#6f8087', '#0b1620',
];

let currentLayer = null;
let userLayerManager = null;
let activeTab = 'single';

let single = null;
let graduated = null;
let categorical = null;
let bivariate = null;

let lastBreaks = null;
let lastCategories = null;
let lastBivariate = null;   // { breaksA, breaksB, minA, maxA, minB, maxB, palette }

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

function geomKind(layer) {
  const t = layer.geometry_type || '';
  if (/Polygon/i.test(t)) return 'polygon';
  if (/LineString/i.test(t)) return 'line';
  if (/Point/i.test(t)) return 'point';
  return 'unknown';
}

function numericColumns(layer) {
  return Object.entries(layer.attribute_schema || {})
    .filter(([, dtype]) => isNumeric(dtype))
    .map(([c]) => c);
}

function allColumns(layer) {
  return Object.keys(layer.attribute_schema || {});
}

function defaultSingle(layer) {
  const palette = ['#04648d','#98c262','#4ea3ce','#e0a93a','#d65a51','#7a9bbb','#b8d290','#5b7d8c'];
  return {
    color:        palette[layer.id % palette.length],
    opacity:      0.9,
    radius:       5,
    lineWidth:    1.5,
    fillOpacity:  0.25,
  };
}

function defaultSizeRange(kind) {
  if (kind === 'point') return { min: 3, max: 16 };
  if (kind === 'line')  return { min: 1, max: 8 };
  return { min: 0, max: 0 };
}

function defaultGraduated(layer) {
  const cols = numericColumns(layer);
  const kind = geomKind(layer);
  const { min, max } = defaultSizeRange(kind);
  return {
    column:  cols[0] || null,
    method:  'quantile',
    bins:    5,
    ramp:    'sand',
    varies:  kind === 'polygon' ? 'color' : 'color',  // user can flip to size/both
    sizeMin: min,
    sizeMax: max,
  };
}

function defaultCategorical(layer) {
  return {
    column:       allColumns(layer)[0] || null,
    mapping:      [],
    defaultColor: '#5b7d8c',
  };
}

function defaultBivariate(layer) {
  const cols = numericColumns(layer);
  return {
    columnA: cols[0] || null,
    columnB: cols[1] || cols[0] || null,
    palette: 'pink-cyan',
    method:  'quantile',
  };
}

// ---------------------------------------------------------------------------
// Public entry
// ---------------------------------------------------------------------------

export function openStyleModal(layer, opts = {}) {
  currentLayer = layer;
  userLayerManager = opts.userLayerManager;
  ensureMounted();

  const saved = getStyle(layer.id);
  single      = { ...defaultSingle(layer) };
  graduated   = { ...defaultGraduated(layer) };
  categorical = { ...defaultCategorical(layer) };
  bivariate   = { ...defaultBivariate(layer) };

  if (saved?.type === 'bivariate') {
    activeTab = 'bivariate';
    Object.assign(bivariate, {
      columnA: saved.columnA ?? bivariate.columnA,
      columnB: saved.columnB ?? bivariate.columnB,
      palette: saved.palette ?? bivariate.palette,
      method:  saved.method  ?? 'quantile',
    });
  } else if (saved?.type === 'graduated' || saved?.breaks) {
    activeTab = 'graduated';
    Object.assign(graduated, {
      column:  saved.column ?? graduated.column,
      method:  saved.method ?? 'quantile',
      bins:    saved.bins   ?? 5,
      ramp:    saved.ramp   ?? 'sand',
      varies:  saved.varies ?? 'color',
      sizeMin: saved.sizeMin ?? graduated.sizeMin,
      sizeMax: saved.sizeMax ?? graduated.sizeMax,
    });
  } else if (saved?.type === 'categorical') {
    activeTab = 'categorical';
    Object.assign(categorical, {
      column:       saved.column,
      mapping:      saved.mapping || [],
      defaultColor: saved.defaultColor || '#5b7d8c',
    });
  } else if (saved?.type === 'single') {
    activeTab = 'single';
    Object.assign(single, saved);
  } else {
    activeTab = 'single';
  }

  document.getElementById('csmTitle').textContent = `Style · ${layer.name}`;
  document.getElementById('styleModal').classList.add('show');
  renderShell();
}

export function closeStyleModal() {
  document.getElementById('styleModal')?.classList.remove('show');
}

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------

function ensureMounted() {
  if (document.getElementById('styleModal')) return;
  document.body.insertAdjacentHTML('beforeend', `
    <div class="modal-bg" id="styleModal">
      <div class="modal csm-modal">
        <div class="csm-head">
          <h3 id="csmTitle">Style</h3>
          <button class="rpanel-close" data-action="closeStyleModal">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="csm-tabs" id="csmTabs"></div>
        <div class="csm-body" id="csmBody"></div>
      </div>
    </div>
    <style>
      .csm-modal{ width:560px; max-width:96vw; padding:0; }
      .csm-head{ display:flex; align-items:baseline; justify-content:space-between; padding:18px 22px 12px; }
      .csm-head h3{ margin:0; }

      .csm-tabs{ display:flex; gap:2px; padding:0 22px; border-bottom:1px solid var(--border); }
      .csm-tab{
        padding:10px 16px; border:none; background:none;
        font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:0.12em;
        color:var(--text3); border-bottom:1.5px solid transparent;
        font-family:var(--font); cursor:pointer;
        transition:color .15s var(--ease), border-color .15s var(--ease);
      }
      .csm-tab:hover{ color:var(--text2); }
      .csm-tab.on{ color:var(--text); border-bottom-color:var(--primary); }

      .csm-body{ padding:20px 22px; max-height:70vh; overflow-y:auto; }
      .csm-row{ display:flex; align-items:center; gap:12px; margin-bottom:14px; }
      .csm-row > label{
        width:100px; flex-shrink:0; font-size:9.5px; font-weight:600;
        text-transform:uppercase; letter-spacing:0.12em; color:var(--text3);
      }
      .csm-row select, .csm-row input[type=text], .csm-row input[type=number]{
        flex:1; background:var(--bg3); border:1px solid var(--border2); color:var(--text);
        padding:8px 10px; border-radius:var(--radius); font-size:12.5px; font-family:var(--font);
      }
      .csm-row select:focus, .csm-row input:focus{ outline:none; border-color:var(--primary); }
      .csm-row input[type=range]{ flex:1; accent-color:var(--primary); height:3px; }
      .csm-row .csm-readout{
        font-family:var(--mono); font-size:11px; color:var(--text2);
        min-width:38px; text-align:right; font-feature-settings:'tnum';
      }

      .csm-radio{ display:flex; gap:8px; flex:1; }
      .csm-radio button{
        flex:1; background:var(--bg3); border:1px solid var(--border2); color:var(--text2);
        padding:7px 10px; border-radius:var(--radius); font-size:12px; font-family:var(--font); cursor:pointer;
        font-weight:500; transition:all .15s var(--ease);
      }
      .csm-radio button:hover{ background:var(--bg4); color:var(--text); }
      .csm-radio button.on{ background:var(--primary-dim); color:var(--primary); border-color:var(--primary); font-weight:600; }

      .csm-color-block{ display:flex; gap:10px; align-items:flex-start; flex:1; }
      .csm-swatches{ display:grid; grid-template-columns:repeat(8, 1fr); gap:6px; flex:1; }
      .csm-swatch{
        aspect-ratio:1; border-radius:var(--radius-sm); cursor:pointer;
        border:1.5px solid transparent; transition:transform .12s var(--ease), border-color .12s var(--ease);
      }
      .csm-swatch:hover{ transform:scale(1.08); }
      .csm-swatch.on{ border-color:var(--text); transform:scale(1.05); }

      .csm-hex-input{
        width:84px; flex-shrink:0; background:var(--bg3); border:1px solid var(--border2);
        color:var(--text); padding:6px 9px; border-radius:var(--radius);
        font-family:var(--mono); font-size:11.5px; text-transform:uppercase;
      }
      .csm-hex-input:focus{ outline:none; border-color:var(--primary); }
      .csm-hex-input.invalid{ border-color:var(--danger); }

      .csm-ramps{ display:grid; grid-template-columns:1fr 1fr; gap:8px; flex:1; }
      .csm-ramp{
        display:flex; align-items:center; gap:10px; padding:8px 10px;
        background:var(--bg3); border:1px solid var(--border2); border-radius:var(--radius);
        cursor:pointer; transition:border-color .15s var(--ease);
      }
      .csm-ramp:hover{ border-color:var(--border3); }
      .csm-ramp.on{ border-color:var(--primary); background:var(--primary-dim); }
      .csm-ramp-label{ font-size:11px; color:var(--text2); }
      .csm-ramp.on .csm-ramp-label{ color:var(--text); font-weight:600; }
      .csm-ramp-strip{ height:14px; flex:1; border-radius:2px; display:grid; grid-template-columns:repeat(9, 1fr); }
      .csm-ramp-strip span{ display:block; height:100%; }

      .csm-preview{
        margin-top:18px; padding:14px;
        background:var(--bg3); border:1px solid var(--border); border-radius:var(--radius-lg);
      }
      .csm-preview-head{
        font-size:9.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.12em;
        color:var(--text3); margin-bottom:10px;
      }
      .csm-bins{ display:flex; flex-direction:column; gap:5px; font-size:11px; font-feature-settings:'tnum'; }
      .csm-bin{ display:flex; align-items:center; gap:10px; }
      .csm-bin-sw{ width:28px; height:12px; border-radius:2px; flex-shrink:0; }
      .csm-bin-range{ color:var(--text2); font-family:var(--mono); flex:1; }
      .csm-bin-size{ color:var(--text3); font-family:var(--mono); font-size:10px; }
      .csm-status{ font-size:11px; color:var(--text3); padding:6px 0; }

      .csm-cat-list{ display:flex; flex-direction:column; gap:6px; }
      .csm-cat-row{
        display:grid; grid-template-columns:24px 1fr 80px 60px;
        gap:10px; align-items:center;
        padding:7px 10px; background:var(--bg3); border:1px solid var(--border);
        border-radius:var(--radius);
      }
      .csm-cat-color{
        width:18px; height:18px; border-radius:3px; cursor:pointer;
        border:1.5px solid transparent;
      }
      .csm-cat-color:hover{ border-color:var(--text); }
      .csm-cat-value{
        font-size:12px; color:var(--text); font-weight:500;
        white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
      }
      .csm-cat-count{
        font-family:var(--mono); font-size:10.5px; color:var(--text3);
        font-feature-settings:'tnum'; text-align:right;
      }
      .csm-cat-hex{
        font-family:var(--mono); font-size:11px; text-transform:uppercase;
        background:var(--bg4); border:1px solid var(--border); color:var(--text);
        padding:5px 7px; border-radius:var(--radius-sm); width:100%;
      }
      .csm-cat-hex:focus{ outline:none; border-color:var(--primary); }
      .csm-cat-hex.invalid{ border-color:var(--danger); }

      .csm-actions{
        display:flex; justify-content:space-between; align-items:center; gap:8px;
        margin-top:18px; padding-top:14px; border-top:1px solid var(--border);
      }
    </style>
  `);
  document.getElementById('styleModal').addEventListener('click', e => {
    if (e.target.id === 'styleModal') closeStyleModal();
  });
}

// ---------------------------------------------------------------------------
// Shell + tab routing
// ---------------------------------------------------------------------------

function renderShell() {
  const tabs = document.getElementById('csmTabs');
  // Bivariate is meaningful only for polygons (it colors the fill); skip the
  // tab button for points/lines so users don't pick something that won't apply.
  const showBivariate = geomKind(currentLayer) === 'polygon'
    && numericColumns(currentLayer).length >= 2;

  tabs.innerHTML = `
    <button class="csm-tab ${activeTab === 'single' ? 'on' : ''}"      data-tab="single">Single</button>
    <button class="csm-tab ${activeTab === 'graduated' ? 'on' : ''}"   data-tab="graduated">Graduated</button>
    <button class="csm-tab ${activeTab === 'categorical' ? 'on' : ''}" data-tab="categorical">Categorical</button>
    ${showBivariate ? `<button class="csm-tab ${activeTab === 'bivariate' ? 'on' : ''}" data-tab="bivariate">Bivariate</button>` : ''}
  `;
  tabs.onclick = e => {
    const t = e.target.closest('[data-tab]');
    if (!t) return;
    activeTab = t.dataset.tab;
    renderShell();
  };

  if (activeTab === 'single')           renderSingleForm();
  else if (activeTab === 'graduated')   renderGraduatedForm();
  else if (activeTab === 'bivariate')   renderBivariateForm();
  else                                  renderCategoricalForm();
}

// ===========================================================================
// SINGLE
// ===========================================================================

function colorPickerBlock(currentColor, onChange) {
  const swatches = PRESET_COLORS.map(c => `
    <div class="csm-swatch ${c.toLowerCase() === currentColor.toLowerCase() ? 'on' : ''}"
         style="background:${c}" data-color="${c}"></div>
  `).join('');
  return `
    <div class="csm-color-block">
      <div class="csm-swatches">${swatches}</div>
      <input class="csm-hex-input" type="text" maxlength="7"
             value="${escape(currentColor)}" placeholder="#hex">
    </div>
  `;
}

function bindColorPicker(scope, onChange, getCurrent) {
  scope.querySelectorAll('[data-color]').forEach(el => {
    el.addEventListener('click', () => {
      const c = el.dataset.color;
      onChange(c);
      // refresh active state
      scope.querySelectorAll('[data-color]').forEach(s =>
        s.classList.toggle('on', s.dataset.color.toLowerCase() === c.toLowerCase())
      );
      const hex = scope.querySelector('.csm-hex-input');
      if (hex) hex.value = c;
    });
  });
  const hex = scope.querySelector('.csm-hex-input');
  if (hex) {
    hex.addEventListener('input', () => {
      const v = hex.value.trim();
      if (HEX_RE.test(v)) {
        hex.classList.remove('invalid');
        onChange(v);
        scope.querySelectorAll('[data-color]').forEach(s =>
          s.classList.toggle('on', s.dataset.color.toLowerCase() === v.toLowerCase())
        );
      } else if (v) {
        hex.classList.add('invalid');
      } else {
        hex.classList.remove('invalid');
      }
    });
  }
}

function renderSingleForm() {
  const kind = geomKind(currentLayer);

  const sizeRow = (() => {
    if (kind === 'point') return `
      <div class="csm-row">
        <label>Radius</label>
        <input type="range" min="2" max="20" step="1" value="${single.radius}" data-bind="radius">
        <span class="csm-readout">${single.radius}px</span>
      </div>
    `;
    if (kind === 'line') return `
      <div class="csm-row">
        <label>Width</label>
        <input type="range" min="1" max="10" step="0.5" value="${single.lineWidth}" data-bind="lineWidth">
        <span class="csm-readout">${single.lineWidth}px</span>
      </div>
    `;
    if (kind === 'polygon') return `
      <div class="csm-row">
        <label>Fill opacity</label>
        <input type="range" min="0" max="1" step="0.05" value="${single.fillOpacity}" data-bind="fillOpacity">
        <span class="csm-readout">${(single.fillOpacity * 100).toFixed(0)}%</span>
      </div>
      <div class="csm-row">
        <label>Outline width</label>
        <input type="range" min="0" max="6" step="0.5" value="${single.lineWidth}" data-bind="lineWidth">
        <span class="csm-readout">${single.lineWidth}px</span>
      </div>
    `;
    return '';
  })();

  const opacityRow = kind === 'polygon' ? '' : `
    <div class="csm-row">
      <label>Opacity</label>
      <input type="range" min="0" max="1" step="0.05" value="${single.opacity}" data-bind="opacity">
      <span class="csm-readout">${(single.opacity * 100).toFixed(0)}%</span>
    </div>
  `;

  document.getElementById('csmBody').innerHTML = `
    <div class="csm-row" style="align-items:flex-start">
      <label style="margin-top:4px">Color</label>
      ${colorPickerBlock(single.color)}
    </div>
    ${opacityRow}
    ${sizeRow}
    <div class="csm-actions">
      <button class="btn-sm btn-outline" data-action="resetStyle">Reset to default</button>
      <div style="display:flex;gap:8px">
        <button class="btn-sm btn-outline" data-action="closeStyleModal">Close</button>
        <button class="btn-sm btn-run" id="csmApplySingle">Apply</button>
      </div>
    </div>
  `;
  attachSingleHandlers();
}

function attachSingleHandlers() {
  const body = document.getElementById('csmBody');
  bindColorPicker(body, c => {
    single.color = c;
    if (userLayerManager?.isLoaded(currentLayer.id)) {
      userLayerManager.applySingleStyle(currentLayer.id, { color: c });
    }
  });

  body.querySelectorAll('[data-bind]').forEach(input => {
    input.addEventListener('input', () => {
      const key = input.dataset.bind;
      single[key] = +input.value;
      const readout = input.parentElement.querySelector('.csm-readout');
      if (readout) {
        if (key === 'opacity' || key === 'fillOpacity') readout.textContent = `${(single[key] * 100).toFixed(0)}%`;
        else readout.textContent = `${single[key]}px`;
      }
      if (userLayerManager?.isLoaded(currentLayer.id)) {
        userLayerManager.applySingleStyle(currentLayer.id, { [key]: single[key] });
      }
    });
  });

  document.getElementById('csmApplySingle').addEventListener('click', applySingleClick);
  document.querySelectorAll('[data-action="resetStyle"]').forEach(b =>
    b.addEventListener('click', resetStyleClick)
  );
}

function applySingleClick() {
  if (!userLayerManager.isLoaded(currentLayer.id)) {
    toast('Toggle the layer onto the map first.');
    return;
  }
  userLayerManager.applySingleStyle(currentLayer.id, single);
  setStyle(currentLayer.id, { type: 'single', ...single });
  resetLegend();
  toast(`Style applied to "${currentLayer.name}"`);
  closeStyleModal();
}

function resetStyleClick() {
  userLayerManager.resetStyle(currentLayer.id);
  clearStyle(currentLayer.id);
  resetLegend();
  toast('Style cleared');
  closeStyleModal();
}

// ===========================================================================
// GRADUATED
// ===========================================================================

function renderGraduatedForm() {
  const numericCols = numericColumns(currentLayer);
  const kind = geomKind(currentLayer);

  if (numericCols.length === 0) {
    document.getElementById('csmBody').innerHTML = `
      <p style="font-size:12.5px;color:var(--text3);line-height:1.55;margin:0">
        This layer has no numeric columns to drive a graduated style.
        Use <b>Single</b> or <b>Categorical</b>, or upload data with a numeric attribute.
      </p>
      <div class="csm-actions">
        <button class="btn-sm btn-outline" data-action="closeStyleModal">Close</button>
      </div>
    `;
    return;
  }

  const colOptions = numericCols.map(c =>
    `<option value="${escape(c)}" ${c === graduated.column ? 'selected' : ''}>${escape(c)}</option>`
  ).join('');

  const rampGrid = Object.keys(RAMPS).map(key => {
    const sw = RAMPS[key].map(c => `<span style="background:${c}"></span>`).join('');
    return `
      <div class="csm-ramp ${graduated.ramp === key ? 'on' : ''}" data-ramp="${key}">
        <div class="csm-ramp-strip">${sw}</div>
        <div class="csm-ramp-label">${escape(RAMP_LABELS[key])}</div>
      </div>
    `;
  }).join('');

  // Vary controls only matter for points + lines
  const showVary = kind === 'point' || kind === 'line';
  const varyRow = showVary ? `
    <div class="csm-row">
      <label>Vary</label>
      <div class="csm-radio">
        <button data-vary="color" class="${graduated.varies === 'color' ? 'on' : ''}">Color</button>
        <button data-vary="size"  class="${graduated.varies === 'size'  ? 'on' : ''}">${kind === 'line' ? 'Width' : 'Size'}</button>
        <button data-vary="both"  class="${graduated.varies === 'both'  ? 'on' : ''}">Both</button>
      </div>
    </div>
  ` : '';

  const sizeRangeRow = (showVary && (graduated.varies === 'size' || graduated.varies === 'both')) ? `
    <div class="csm-row">
      <label>Size range</label>
      <input type="number" min="0.5" max="40" step="0.5" value="${graduated.sizeMin}" id="csmSizeMin" style="max-width:80px">
      <span style="color:var(--text3);font-size:11px">to</span>
      <input type="number" min="0.5" max="40" step="0.5" value="${graduated.sizeMax}" id="csmSizeMax" style="max-width:80px">
      <span style="color:var(--text3);font-size:11px">${kind === 'line' ? 'px width' : 'px radius'}</span>
    </div>
  ` : '';

  const showColorRamp = !showVary || graduated.varies !== 'size';

  document.getElementById('csmBody').innerHTML = `
    <div class="csm-row">
      <label>Color by</label>
      <select id="csmColumn">${colOptions}</select>
    </div>
    <div class="csm-row">
      <label>Method</label>
      <div class="csm-radio">
        <button data-method="quantile" class="${graduated.method === 'quantile' ? 'on' : ''}">Quantile</button>
        <button data-method="equal"    class="${graduated.method === 'equal'    ? 'on' : ''}">Equal interval</button>
      </div>
    </div>
    <div class="csm-row">
      <label>Bins</label>
      <select id="csmBins">
        ${[3,4,5,6,7,8,9].map(n => `<option value="${n}" ${graduated.bins === n ? 'selected' : ''}>${n}</option>`).join('')}
      </select>
    </div>
    ${varyRow}
    ${sizeRangeRow}
    ${showColorRamp ? `
      <div class="csm-row" style="align-items:flex-start">
        <label style="margin-top:8px">Ramp</label>
        <div class="csm-ramps">${rampGrid}</div>
      </div>
    ` : ''}

    <div class="csm-preview" id="csmPreview">
      <div class="csm-preview-head">Preview</div>
      <div class="skel skel-text-line" style="height:11px;width:88%;margin-bottom:7px"></div>
      <div class="skel skel-text-line" style="height:11px;width:74%;margin-bottom:7px"></div>
      <div class="skel skel-text-line" style="height:11px;width:80%"></div>
    </div>

    <div class="csm-actions">
      <button class="btn-sm btn-outline" data-action="resetStyle">Reset to default</button>
      <div style="display:flex;gap:8px">
        <button class="btn-sm btn-outline" data-action="closeStyleModal">Close</button>
        <button class="btn-sm btn-run" id="csmApply">Apply</button>
      </div>
    </div>
  `;

  attachGraduatedHandlers();
  updateGraduatedPreview();
}

function attachGraduatedHandlers() {
  document.getElementById('csmColumn').addEventListener('change', e => {
    graduated.column = e.target.value; updateGraduatedPreview();
  });
  document.getElementById('csmBins').addEventListener('change', e => {
    graduated.bins = +e.target.value; updateGraduatedPreview();
  });
  document.querySelectorAll('[data-method]').forEach(btn => {
    btn.addEventListener('click', () => {
      graduated.method = btn.dataset.method;
      document.querySelectorAll('[data-method]').forEach(b => b.classList.toggle('on', b === btn));
      updateGraduatedPreview();
    });
  });
  document.querySelectorAll('[data-vary]').forEach(btn => {
    btn.addEventListener('click', () => {
      graduated.varies = btn.dataset.vary;
      renderGraduatedForm();   // re-render to show/hide size range + ramp
    });
  });
  document.getElementById('csmSizeMin')?.addEventListener('input', e => {
    graduated.sizeMin = +e.target.value; updateGraduatedPreview();
  });
  document.getElementById('csmSizeMax')?.addEventListener('input', e => {
    graduated.sizeMax = +e.target.value; updateGraduatedPreview();
  });
  document.querySelectorAll('[data-ramp]').forEach(el => {
    el.addEventListener('click', () => {
      graduated.ramp = el.dataset.ramp;
      document.querySelectorAll('[data-ramp]').forEach(e => e.classList.toggle('on', e === el));
      updateGraduatedPreview();
    });
  });
  document.getElementById('csmApply').addEventListener('click', applyGraduatedClick);
  document.querySelectorAll('[data-action="resetStyle"]').forEach(b =>
    b.addEventListener('click', resetStyleClick)
  );
}

async function updateGraduatedPreview() {
  const preview = document.getElementById('csmPreview');
  preview.innerHTML = `
    <div class="csm-preview-head">Preview</div>
    <div class="skel skel-text-line" style="height:11px;width:88%;margin-bottom:7px"></div>
    <div class="skel skel-text-line" style="height:11px;width:74%;margin-bottom:7px"></div>
    <div class="skel skel-text-line" style="height:11px;width:80%"></div>
  `;
  try {
    const result = await runBreaks(currentLayer.id, {
      column: graduated.column,
      method: graduated.method,
      bins:   graduated.bins,
    });
    lastBreaks = result;
    renderGraduatedPreview(result);
  } catch (e) {
    lastBreaks = null;
    preview.innerHTML = `<div class="alert alert-error">${escape(e.message)}</div>`;
  }
}

function renderGraduatedPreview(b) {
  if (!b.breaks || b.breaks.length === 0) {
    document.getElementById('csmPreview').innerHTML = `
      <div class="csm-preview-head">Preview</div>
      <div class="csm-status">All values equal — only one bin would render.</div>
    `;
    return;
  }
  const colors = sampleRamp(graduated.ramp, b.bins);
  const sizes  = rampedSizes(graduated.sizeMin, graduated.sizeMax, b.bins);
  const edges  = [b.min, ...b.breaks, b.max];
  const fmt = v => v === null ? '—' : (Number.isInteger(v) ? v.toLocaleString() : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));

  const showColor = graduated.varies !== 'size';
  const showSize  = graduated.varies === 'size' || graduated.varies === 'both';

  const rows = colors.map((c, i) => `
    <div class="csm-bin">
      <div class="csm-bin-sw" style="background:${showColor ? c : 'transparent'};${!showColor ? 'border:1px solid var(--border2)' : ''}"></div>
      <div class="csm-bin-range">${fmt(edges[i])} → ${fmt(edges[i + 1])}</div>
      ${showSize ? `<div class="csm-bin-size">${sizes[i]}px</div>` : ''}
    </div>
  `).join('');
  const nullsNote = b.null_count > 0
    ? `<div class="csm-status" style="margin-top:8px">${b.null_count.toLocaleString()} null${b.null_count === 1 ? '' : 's'} of ${b.total_count.toLocaleString()} ignored</div>`
    : '';
  document.getElementById('csmPreview').innerHTML = `
    <div class="csm-preview-head">Preview · ${escape(b.method)}, ${b.bins} bins · ${escape(graduated.varies)}</div>
    <div class="csm-bins">${rows}</div>
    ${nullsNote}
  `;
}

function applyGraduatedClick() {
  if (!lastBreaks?.breaks) return;
  const btn = document.getElementById('csmApply');
  btn?.classList.add('btn-loading');

  const colors = sampleRamp(graduated.ramp, lastBreaks.bins);
  const colorExpr = graduated.varies !== 'size'
    ? stepExpression(graduated.column, lastBreaks.breaks, colors)
    : undefined;
  const sizeExpr = (graduated.varies === 'size' || graduated.varies === 'both')
    ? stepExpression(graduated.column, lastBreaks.breaks,
                     rampedSizes(graduated.sizeMin, graduated.sizeMax, lastBreaks.bins))
    : undefined;

  const ok = userLayerManager.applyGraduated(currentLayer.id, { colorExpr, sizeExpr });
  btn?.classList.remove('btn-loading');
  if (!ok) {
    toast('Toggle the layer onto the map first.');
    return;
  }

  setStyle(currentLayer.id, {
    type: 'graduated',
    column: graduated.column, method: graduated.method,
    bins: graduated.bins, ramp: graduated.ramp,
    varies: graduated.varies,
    sizeMin: graduated.sizeMin, sizeMax: graduated.sizeMax,
    breaks: lastBreaks.breaks, min: lastBreaks.min, max: lastBreaks.max,
  });
  pushChoroplethLegend(currentLayer.name, graduated.column, lastBreaks, colors);
  toast(`Graduated style applied to "${currentLayer.name}"`);
  closeStyleModal();
}

// ===========================================================================
// CATEGORICAL
// ===========================================================================

function renderCategoricalForm() {
  const cols = allColumns(currentLayer);
  if (cols.length === 0) {
    document.getElementById('csmBody').innerHTML = `
      <p style="font-size:12.5px;color:var(--text3)">No attributes on this layer.</p>
    `;
    return;
  }
  const colOptions = cols.map(c =>
    `<option value="${escape(c)}" ${c === categorical.column ? 'selected' : ''}>${escape(c)}</option>`
  ).join('');

  document.getElementById('csmBody').innerHTML = `
    <p style="font-size:11.5px;color:var(--text3);margin:0 0 14px">
      Pick a column whose values define your categories — each distinct
      value becomes its own color. Tap a swatch or type a hex to override.
    </p>

    <div class="csm-row">
      <label>Column</label>
      <select id="csmCatColumn">${colOptions}</select>
    </div>

    <div class="csm-row">
      <label>Default</label>
      <input class="csm-cat-hex" id="csmCatDefault" type="text"
             value="${escape(categorical.defaultColor)}" maxlength="7"
             style="width:100px;flex:0">
      <span style="color:var(--text3);font-size:11px;flex:1">color used for any unmapped value</span>
    </div>

    <div id="csmCatList" class="csm-cat-list">
      <div class="csm-status">Loading distinct values…</div>
    </div>

    <div class="csm-actions">
      <button class="btn-sm btn-outline" data-action="resetStyle">Reset to default</button>
      <div style="display:flex;gap:8px">
        <button class="btn-sm btn-outline" data-action="closeStyleModal">Close</button>
        <button class="btn-sm btn-run" id="csmApplyCategorical">Apply</button>
      </div>
    </div>
  `;

  attachCategoricalHandlers();
  loadCategoricalValues();
}

function attachCategoricalHandlers() {
  document.getElementById('csmCatColumn').addEventListener('change', e => {
    categorical.column = e.target.value;
    categorical.mapping = [];
    loadCategoricalValues();
  });
  document.getElementById('csmCatDefault').addEventListener('input', e => {
    const v = e.target.value.trim();
    if (HEX_RE.test(v)) {
      e.target.classList.remove('invalid');
      categorical.defaultColor = v;
    } else {
      e.target.classList.add('invalid');
    }
  });
  document.getElementById('csmApplyCategorical').addEventListener('click', applyCategoricalClick);
  document.querySelectorAll('[data-action="resetStyle"]').forEach(b =>
    b.addEventListener('click', resetStyleClick)
  );
}

async function loadCategoricalValues() {
  const list = document.getElementById('csmCatList');
  list.innerHTML = `<div class="csm-status">Loading distinct values…</div>`;
  try {
    const res = await runCategories(currentLayer.id, {
      column: categorical.column, limit: 50,
    });
    lastCategories = res;
    // Seed mapping if empty (or column changed)
    if (!categorical.mapping.length || categorical.mapping[0]?.column !== categorical.column) {
      categorical.mapping = res.values.map((row, i) => ({
        value: row.value,
        count: row.count,
        color: pickCategoricalColor(i),
      }));
    } else {
      // Merge: keep existing colors, add any new values
      const existing = new Map(categorical.mapping.map(m => [String(m.value), m]));
      categorical.mapping = res.values.map((row, i) => {
        const prev = existing.get(String(row.value));
        return {
          value: row.value,
          count: row.count,
          color: prev?.color ?? pickCategoricalColor(i),
        };
      });
    }
    renderCategoricalList(res);
  } catch (e) {
    list.innerHTML = `<div class="alert alert-error">${escape(e.message)}</div>`;
  }
}

function renderCategoricalList(res) {
  const list = document.getElementById('csmCatList');
  const overflow = res.distinct_count > res.values.length
    ? `<div class="csm-status">${res.distinct_count.toLocaleString()} distinct values — showing top ${res.values.length}.</div>`
    : '';
  const rows = categorical.mapping.map((m, idx) => `
    <div class="csm-cat-row" data-cat-idx="${idx}">
      <div class="csm-cat-color" style="background:${m.color}" data-cat-swatch="${idx}"></div>
      <div class="csm-cat-value">${m.value === null ? '<em style="color:var(--text3)">null</em>' : escape(String(m.value))}</div>
      <input class="csm-cat-hex" type="text" maxlength="7" value="${escape(m.color)}" data-cat-hex="${idx}">
      <div class="csm-cat-count">${m.count.toLocaleString()}</div>
    </div>
  `).join('');
  list.innerHTML = rows + overflow;

  // Swatch click cycles through palette
  list.querySelectorAll('[data-cat-swatch]').forEach(el => {
    el.addEventListener('click', () => {
      const idx = +el.dataset.catSwatch;
      const next = pickCategoricalColor(
        (CATEGORICAL_PALETTE.indexOf(categorical.mapping[idx].color) + 1) % CATEGORICAL_PALETTE.length
      );
      categorical.mapping[idx].color = next;
      el.style.background = next;
      const hex = list.querySelector(`[data-cat-hex="${idx}"]`);
      if (hex) hex.value = next;
    });
  });

  // Hex input
  list.querySelectorAll('[data-cat-hex]').forEach(input => {
    input.addEventListener('input', () => {
      const idx = +input.dataset.catHex;
      const v = input.value.trim();
      if (HEX_RE.test(v)) {
        input.classList.remove('invalid');
        categorical.mapping[idx].color = v;
        const sw = list.querySelector(`[data-cat-swatch="${idx}"]`);
        if (sw) sw.style.background = v;
      } else {
        input.classList.add('invalid');
      }
    });
  });
}

function applyCategoricalClick() {
  if (!userLayerManager.isLoaded(currentLayer.id)) {
    toast('Toggle the layer onto the map first.');
    return;
  }
  const expr = matchExpression(categorical.column, categorical.mapping, categorical.defaultColor);
  userLayerManager.applyCategorical(currentLayer.id, expr);

  setStyle(currentLayer.id, {
    type: 'categorical',
    column: categorical.column,
    mapping: categorical.mapping.map(({value, color}) => ({value, color})),
    defaultColor: categorical.defaultColor,
  });
  pushCategoricalLegend(currentLayer.name, categorical.column, categorical.mapping, categorical.defaultColor);
  toast(`Categorical style applied to "${currentLayer.name}"`);
  closeStyleModal();
}

// ===========================================================================
// BIVARIATE
// ===========================================================================

function renderBivariateForm() {
  const cols = numericColumns(currentLayer);
  if (cols.length < 2) {
    document.getElementById('csmBody').innerHTML = `
      <p style="font-size:12.5px;color:var(--text3)">
        Bivariate maps need at least two numeric columns on this layer.
      </p>
    `;
    return;
  }

  const colOptions = (selected) => cols.map(c =>
    `<option value="${escape(c)}" ${c === selected ? 'selected' : ''}>${escape(c)}</option>`
  ).join('');

  const paletteSwatches = Object.entries(BIVARIATE_PALETTES).map(([key, grid]) => {
    const cells = [];
    for (let r = grid.length - 1; r >= 0; r--) {
      for (let c = 0; c < grid[0].length; c++) {
        cells.push(`<div class="csm-bv-cell" style="background:${grid[r][c]}"></div>`);
      }
    }
    return `
      <button type="button" class="csm-bv-pick ${key === bivariate.palette ? 'on' : ''}" data-palette="${key}" title="${escape(BIVARIATE_PALETTE_LABELS[key])}">
        <div class="csm-bv-mini">${cells.join('')}</div>
        <span>${escape(BIVARIATE_PALETTE_LABELS[key])}</span>
      </button>
    `;
  }).join('');

  document.getElementById('csmBody').innerHTML = `
    <p style="font-size:11.5px;color:var(--text3);margin:0 0 14px">
      Color polygons by two numeric columns at once. Each axis is split into 3 quantile bins, producing 9 combined classes.
    </p>

    <div class="csm-row">
      <label>Variable A (rows)</label>
      <select id="csmBvA">${colOptions(bivariate.columnA)}</select>
    </div>
    <div class="csm-row">
      <label>Variable B (cols)</label>
      <select id="csmBvB">${colOptions(bivariate.columnB)}</select>
    </div>

    <div style="font-size:9.5px;text-transform:uppercase;letter-spacing:0.14em;color:var(--text3);margin:14px 0 8px;font-weight:600">Palette</div>
    <div class="csm-bv-picks">${paletteSwatches}</div>

    <div id="csmPreview" class="csm-preview" style="margin-top:14px"></div>

    <div class="csm-foot">
      <button class="btn-sm" data-action="resetStyle">Reset</button>
      <button id="csmApplyBivariate" class="btn-sm btn-run">Apply</button>
    </div>

    <style>
      .csm-bv-picks { display:grid; grid-template-columns:repeat(2,1fr); gap:8px; }
      .csm-bv-pick {
        display:flex; align-items:center; gap:10px;
        padding:8px 10px; border:1px solid var(--border); border-radius:6px;
        background:var(--bg3); color:var(--text2); cursor:pointer; text-align:left;
        font-size:11.5px; font-family:var(--font);
        transition: border-color .15s var(--ease), background .15s var(--ease);
      }
      .csm-bv-pick:hover { border-color: var(--border2); }
      .csm-bv-pick.on { border-color: var(--primary); background: var(--bg4); color: var(--text); }
      .csm-bv-mini { display:grid; grid-template-columns:repeat(3,9px); grid-auto-rows:9px; gap:1px; }
      .csm-bv-cell { width:9px; height:9px; }
    </style>
  `;

  document.getElementById('csmBvA').addEventListener('change', e => {
    bivariate.columnA = e.target.value;
    updateBivariatePreview();
  });
  document.getElementById('csmBvB').addEventListener('change', e => {
    bivariate.columnB = e.target.value;
    updateBivariatePreview();
  });
  document.querySelectorAll('[data-palette]').forEach(btn => {
    btn.addEventListener('click', () => {
      bivariate.palette = btn.dataset.palette;
      document.querySelectorAll('[data-palette]').forEach(b =>
        b.classList.toggle('on', b === btn));
      updateBivariatePreview();
    });
  });
  document.getElementById('csmApplyBivariate').addEventListener('click', applyBivariateClick);
  document.querySelectorAll('[data-action="resetStyle"]').forEach(b =>
    b.addEventListener('click', resetStyleClick)
  );

  updateBivariatePreview();
}

async function updateBivariatePreview() {
  const preview = document.getElementById('csmPreview');
  if (!preview) return;
  preview.innerHTML = `
    <div class="csm-preview-head">Preview</div>
    <div class="skel skel-text-line" style="height:11px;width:80%;margin-bottom:7px"></div>
    <div class="skel skel-text-line" style="height:11px;width:70%"></div>
  `;

  if (!bivariate.columnA || !bivariate.columnB) {
    preview.innerHTML = `<div class="csm-preview-head">Preview</div><div class="csm-status">Pick both columns.</div>`;
    return;
  }
  if (bivariate.columnA === bivariate.columnB) {
    preview.innerHTML = `<div class="csm-preview-head">Preview</div><div class="csm-status">Variables A and B must differ.</div>`;
    lastBivariate = null;
    return;
  }

  try {
    const [a, b] = await Promise.all([
      runBreaks(currentLayer.id, { column: bivariate.columnA, method: 'quantile', bins: 3 }),
      runBreaks(currentLayer.id, { column: bivariate.columnB, method: 'quantile', bins: 3 }),
    ]);
    lastBivariate = { a, b };

    const palette = BIVARIATE_PALETTES[bivariate.palette];
    const fmt = v => v === null
      ? '—'
      : (Number.isInteger(v) ? v.toLocaleString() : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));

    // Build a 3x3 swatch grid
    const cells = [];
    for (let r = palette.length - 1; r >= 0; r--) {
      for (let c = 0; c < palette[0].length; c++) {
        cells.push(`<div class="csm-bv-pre-cell" style="background:${palette[r][c]}"></div>`);
      }
    }

    preview.innerHTML = `
      <div class="csm-preview-head">Preview</div>
      <div class="csm-bv-grid-wrap">
        <div class="csm-bv-axis-y">${escape(bivariate.columnA)} →</div>
        <div class="csm-bv-grid">${cells.join('')}</div>
        <div class="csm-bv-axis-x">${escape(bivariate.columnB)} →</div>
      </div>
      <div class="csm-bv-ranges">
        <div><strong>${escape(bivariate.columnA)}</strong>: ${fmt(a.min)} → ${fmt(a.max)} · breaks ${a.breaks.map(fmt).join(', ')}</div>
        <div><strong>${escape(bivariate.columnB)}</strong>: ${fmt(b.min)} → ${fmt(b.max)} · breaks ${b.breaks.map(fmt).join(', ')}</div>
      </div>
      <style>
        .csm-bv-grid-wrap { display:grid; grid-template-columns:auto 1fr; grid-template-rows:auto auto; column-gap:8px; row-gap:6px; align-items:end; margin-top:8px; }
        .csm-bv-axis-y { writing-mode:vertical-rl; transform:rotate(180deg); font-size:10.5px; color:var(--text3); font-family:var(--font-display); font-style:italic; align-self:end; }
        .csm-bv-grid { display:grid; grid-template-columns:repeat(3,28px); grid-auto-rows:28px; gap:2px; }
        .csm-bv-pre-cell { width:28px; height:28px; border-radius:2px; }
        .csm-bv-axis-x { grid-column:2; font-size:10.5px; color:var(--text3); font-family:var(--font-display); font-style:italic; }
        .csm-bv-ranges { font-size:11px; color:var(--text2); font-family:var(--mono); margin-top:10px; line-height:1.6; }
        .csm-bv-ranges strong { color:var(--text); font-family:var(--font); }
      </style>
    `;
  } catch (e) {
    lastBivariate = null;
    preview.innerHTML = `<div class="alert alert-error">${escape(e.message)}</div>`;
  }
}

function applyBivariateClick() {
  if (!lastBivariate?.a?.breaks || !lastBivariate?.b?.breaks) {
    toast('Preview not ready yet.');
    return;
  }
  if (!userLayerManager.isLoaded(currentLayer.id)) {
    toast('Toggle the layer onto the map first.');
    return;
  }
  const palette = BIVARIATE_PALETTES[bivariate.palette];
  const colorExpr = bivariateExpression(
    bivariate.columnA, lastBivariate.a.breaks,
    bivariate.columnB, lastBivariate.b.breaks,
    palette,
  );
  const ok = userLayerManager.applyGraduated(currentLayer.id, { colorExpr });
  if (!ok) {
    toast('Layer not on the map.');
    return;
  }

  setStyle(currentLayer.id, {
    type: 'bivariate',
    columnA: bivariate.columnA,
    columnB: bivariate.columnB,
    palette: bivariate.palette,
    method:  'quantile',
    breaksA: lastBivariate.a.breaks,
    breaksB: lastBivariate.b.breaks,
  });
  pushBivariateLegend(
    currentLayer.name,
    bivariate.columnA, bivariate.columnB,
    palette,
  );
  toast(`Bivariate style applied to "${currentLayer.name}"`);
  closeStyleModal();
}

// ---------------------------------------------------------------------------
// Legend helpers (re-exported, used by userLayers.js for re-apply on show)
// ---------------------------------------------------------------------------

export function pushChoroplethLegend(layerName, column, breaks, colors) {
  const edges = [breaks.min, ...breaks.breaks, breaks.max];
  const fmt = v => v === null ? '—' : (Number.isInteger(v) ? v.toLocaleString() : Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }));
  const items = colors.map((c, i) => ({
    color: c,
    label: `${fmt(edges[i])} – ${fmt(edges[i + 1])}`,
  }));
  setLegend(`${layerName} · ${column}`, items);
}

export function pushCategoricalLegend(layerName, column, mapping, defaultColor) {
  const items = mapping.map(m => ({
    color: m.color,
    label: m.value === null ? 'null' : String(m.value),
  }));
  if (defaultColor) items.push({ color: defaultColor, label: 'other' });
  setLegend(`${layerName} · ${column}`, items);
}

export function pushBivariateLegend(layerName, columnA, columnB, palette) {
  setBivariateLegend(`${layerName}`, palette, columnA, columnB);
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
